import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ResearchWizardStep } from 'buyside/components/presentation/ResearchFormWizard/researchFormWizard';
import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { IdeaFormInput, WhitePaperFormInput } from 'buyside/util/researchSubmission';

import styles from './researchFormWizardStyles.scss';

const ResearchFormWizardSubmissionButtons: FC<{
  currentStep: ResearchWizardStep;
  handleCreateOrUpdateDraft: (
    values: IdeaFormInput & WhitePaperFormInput,
    nextStep: ResearchWizardStep,
    isSubmission: boolean
  ) => Promise<any>;
  handleSetCurrentStep: (step: ResearchWizardStep) => void;
  isDraft: boolean;
  researchPath: string;
  researchType: 'Idea' | 'Macro/Thematic Report';
  steps: ResearchWizardStep[];
}> = ({
  currentStep,
  handleCreateOrUpdateDraft,
  handleSetCurrentStep,
  isDraft,
  researchPath,
  researchType,
  steps
}) => {
  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    setError
  } = useFormContext<IdeaFormInput & WhitePaperFormInput>();

  const currentIndex = steps.indexOf(currentStep);
  const previousStep = steps[currentIndex - 1];
  const nextStep = steps[currentIndex + 1];
  const isLastStep = currentIndex === steps.length - 1;

  const redirectStep: ResearchWizardStep = useMemo(
    () => ({
      name: 'redirect',
      label: '',
      component: (): ReactElement => {
        window.location.assign(researchPath);

        return (
          <div className={styles.redirectStep}>
            <h4>Redirecting to your {researchType}</h4>
            <LoadingSpinner />
          </div>
        );
      }
    }),
    [researchPath, researchType]
  );

  const handleRequiredDisclaimers = useCallback(
    (requiredDisclaimers: {
      employerApprovalDisclaimer: boolean;
      noPriorAgreementDisclaimer: boolean;
      publicInformationDisclaimer: boolean;
    }) => {
      Object.entries(requiredDisclaimers).forEach(([key, value]) => {
        if (!value) {
          setError(
            key as
              | 'employerApprovalDisclaimer'
              | 'noPriorAgreementDisclaimer'
              | 'publicInformationDisclaimer',
            { message: 'Please agree to the disclaimer' }
          );
        }
      });
    },
    [setError]
  );

  const handleUnmodifiedSubmit = useCallback(
    (step: ResearchWizardStep) =>
      step === previousStep
        ? (): void => handleSetCurrentStep(step)
        : handleSubmit(() => {
            handleSetCurrentStep(step);
          }),
    [handleSubmit, handleSetCurrentStep, previousStep]
  );

  const handleSubmitToDraft = useCallback(
    (step: ResearchWizardStep) =>
      handleSubmit(values => handleCreateOrUpdateDraft(values, step, false)),
    [handleCreateOrUpdateDraft, handleSubmit]
  );

  const handleSubmitToOpen = handleSubmit(values => {
    const {
      employerApprovalDisclaimer,
      noPriorAgreementDisclaimer,
      publicInformationDisclaimer
    } = values;

    const requiredDisclaimers = {
      employerApprovalDisclaimer,
      noPriorAgreementDisclaimer,
      publicInformationDisclaimer
    };

    const hasAgreedToDisclaimers = Object.values(requiredDisclaimers).every(Boolean);

    if (!hasAgreedToDisclaimers) return handleRequiredDisclaimers(requiredDisclaimers);

    return handleCreateOrUpdateDraft(values, redirectStep, true);
  });

  const getButtonProps = useCallback(
    (step: ResearchWizardStep, actionLabel: string) => ({
      disabled: isSubmitting,
      onClick: isDirty ? handleSubmitToDraft(step) : handleUnmodifiedSubmit(step),
      text: isDirty ? `Save and ${actionLabel} ${step.label}` : `${actionLabel} ${step.label}`
    }),
    [handleSubmitToDraft, handleUnmodifiedSubmit, isDirty, isSubmitting]
  );

  if (currentStep.name === 'redirect') return null;

  return (
    <div className={styles.researchFormWizardSubmissionButtons}>
      {previousStep ? <Button {...getButtonProps(previousStep, 'Back to')} /> : null}
      {nextStep ? (
        <Button type={BUTTON_TYPES.PRIMARY} {...getButtonProps(nextStep, 'Continue to')} />
      ) : null}
      {isLastStep ? (
        isDraft ? (
          <>
            <Button
              disabled={isSubmitting}
              type={BUTTON_TYPES.PRIMARY}
              onClick={handleSubmitToOpen}
              text={isDirty ? 'Save and Submit Draft' : 'Submit Draft'}
            />
            <Button
              className={styles.previewButton}
              disabled={isSubmitting}
              type={BUTTON_TYPES.MAJOR}
              onClick={
                isDirty ? handleSubmitToDraft(redirectStep) : handleUnmodifiedSubmit(redirectStep)
              }
              text={isDirty ? 'Save and Preview Draft' : 'Preview Draft'}
            />
          </>
        ) : (
          <Button
            disabled={isSubmitting}
            type={BUTTON_TYPES.PRIMARY}
            onClick={isDirty ? handleSubmitToOpen : handleUnmodifiedSubmit(redirectStep)}
            text={
              isDirty ? `Save and Back to Your ${researchType}` : `Back to Your ${researchType}`
            }
          />
        )
      ) : null}
    </div>
  );
};

export default ResearchFormWizardSubmissionButtons;
